import AddIcon from "@mui/icons-material/Add";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  IconButton,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import OperatorAssistedRegistrationApi from "../../../../../infrastructure/api/operatorAssistedRegistrationApi";
import AssistedRegistrationPage from "../../../partner-commission/AssistedRegistrationPage";
import PartnerCommissionPageShell from "../../../partner-commission/components/PartnerCommissionPageShell";
import EnhancedTable from "../../../../components/shared/table";
import {
  ASSISTED_REGISTRATION_NEW_QUERY,
  buildAssistedRegistrationSearchParams,
  readAssistedRegistrationIdFromQuery,
} from "../../../partner-commission/assistedRegistrationRouteUtils";

const LIST_BACK_PATH = "/SLN/SolarentOperator/ApplicantRegistration";

const pick = (row, ...keys) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value != null && value !== "") return value;
  }
  return null;
};

const mapListRow = (item) => ({
  id: pick(item, "leadId", "LeadId"),
  leadCode: pick(item, "leadCode", "LeadCode") || "—",
  fullName: pick(item, "fullName", "FullName") || pick(item, "maskedMobile", "MaskedMobile") || "—",
  maskedMobile: pick(item, "maskedMobile", "MaskedMobile") || "—",
  referenceID: pick(item, "referenceId", "ReferenceId") ?? "—",
  plantCapacityTitle: pick(item, "plantCapacityTitle", "PlantCapacityTitle") || "—",
  statusTitle: pick(item, "requestConfirmTitle", "RequestConfirmTitle") || pick(item, "statusTitle", "StatusTitle") || "—",
  registrationStatusTitle: pick(item, "statusTitle", "StatusTitle") || "—",
  updateOn: pick(item, "updateOn", "UpdateOn", "submittedAt", "SubmittedAt"),
  canEdit: Boolean(item?.canEdit ?? item?.CanEdit),
});

const HEAD_CELLS = [
  { id: "leadCode", label: "کد" },
  { id: "fullName", label: "نام متقاضی" },
  { id: "maskedMobile", label: "موبایل" },
  { id: "referenceID", label: "شماره پرونده" },
  { id: "plantCapacityTitle", label: "ظرفیت" },
  { id: "statusTitle", label: "وضعیت" },
  { id: "registrationStatusTitle", label: "مرحله ثبت‌نام" },
  { id: "actions", label: "عملیات" },
];

const ListSection = () => {
  const [, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await OperatorAssistedRegistrationApi.getList();
      if (res.isSuccess && Array.isArray(res.data)) {
        setRows(res.data.map(mapListRow));
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const navigateToNew = () => {
    setSearchParams({ [ASSISTED_REGISTRATION_NEW_QUERY]: "1" });
  };

  const navigateToDetail = (row) => {
    setSearchParams(buildAssistedRegistrationSearchParams(row.id));
  };

  const columns = useMemo(
    () =>
      HEAD_CELLS.map((hc) => {
        if (hc.id === "actions") {
          return {
            ...hc,
            render: (row) => (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <IconButton size="small" onClick={() => navigateToDetail(row)} title="مشاهده/ویرایش">
                  {row.canEdit ? <EditIcon fontSize="small" /> : <ContentPasteSearchIcon fontSize="small" />}
                </IconButton>
              </Box>
            ),
          };
        }
        return hc;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <PartnerCommissionPageShell
      title="ثبت‌نام متقاضیان"
      description="لیست ثبت‌نام‌هایی که توسط کارشناس برای متقاضیان ثبت شده است."
      loading={loading}
    >
      <Card>
        <CardHeader
          title="ثبت‌نام‌ها"
          action={
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button startIcon={<SyncIcon />} size="small" onClick={fetchList}>
                بروزرسانی
              </Button>
              <Button variant="contained" startIcon={<AddIcon />} size="small" onClick={navigateToNew}>
                ثبت‌نام جدید
              </Button>
            </Box>
          }
        />
        <Divider />
        <CardContent sx={{ p: 0 }}>
          <Grid container>
            <Grid item xs={12}>
              <EnhancedTable
                headCells={columns}
                rows={rows}
                rowKey="id"
                onRowClick={navigateToDetail}
                dense
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </PartnerCommissionPageShell>
  );
};

const OperatorApplicantRegistrationPage = () => {
  const [searchParams] = useSearchParams();
  const registrationId = readAssistedRegistrationIdFromQuery(searchParams);
  const isNew = searchParams.get(ASSISTED_REGISTRATION_NEW_QUERY) === "1";

  if (!registrationId && !isNew) {
    return <ListSection />;
  }

  return (
    <AssistedRegistrationPage
      api={OperatorAssistedRegistrationApi}
      pageShell={PartnerCommissionPageShell}
      title="ثبت نام متقاضی"
      description="ثبت درخواست نیروگاه برای متقاضی توسط کارشناس با تأیید موبایل، تکمیل پروفایل و ارسال فرم درخواست."
      submitLockedMessage="درخواست متقاضی ثبت شد و وارد مرحله بررسی گردید."
      listBackPath={LIST_BACK_PATH}
    />
  );
};

export default OperatorApplicantRegistrationPage;
